import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import type { Hero } from '@mezha/api'
import { Emblem, type EmblemName } from '../ui/brand/Emblems'
import { Gauges, cx, type Resource } from '../ui'
import { clockText } from '../lib/time'
import { InjuryChip } from '../screens/injury/InjuryCard'

type NavItem = { to: string; label: string; short?: string; emblem: EmblemName; more?: boolean; end?: boolean; badge?: string }

export const NAV: NavItem[] = [
  { to: '/play', label: 'Локация', emblem: 'nav.location', end: true },
  { to: '/play/hero', label: 'Персонаж', short: 'Герой', emblem: 'nav.hero' },
  { to: '/play/inventory', label: 'Инвентарь', short: 'Вещи', emblem: 'nav.inventory' },
  { to: '/play/journal', label: 'Журнал', emblem: 'nav.journal' },
  { to: '/play/skills', label: 'Умения', emblem: 'nav.skills', more: true },
  { to: '/play/map', label: 'Карта', emblem: 'nav.map', more: true },
  { to: '/play/arena', label: 'Арена', emblem: 'nav.arena', more: true },
  { to: '/play/clans', label: 'Кланы', emblem: 'nav.clans', more: true },
  { to: '/play/faith', label: 'Боги', emblem: 'nav.gods', more: true },
  { to: '/play/settings', label: 'Настройки', emblem: 'nav.settings', more: true },
]

const KIND = { hp: 'hp', mn: 'mn', st: 'st' } as const

/** Persistent resources exactly as the server gave them, with a "full at"
 *  hint; the shell re-reads the hero at that moment (never extrapolates). */
export function heroResources(hero: Hero | undefined): Resource[] {
  if (!hero) return []
  const order = ['hp', 'st', 'mn'] as const
  return order.flatMap(kind => {
    const r = hero.resources.find(x => x.kind === kind)
    if (!r) return []
    const value = r.value
    const hint = value >= r.max ? 'полностью' : r.full_at ? `полностью к ${clockText(r.full_at)}` : undefined
    return [{ kind: KIND[kind], value, max: r.max, hint }]
  })
}

type Props = {
  hero: Hero | undefined
  heroState: 'loading' | 'ready' | 'error'
  onRetryHero: () => void
  journalBadge?: string
}

function Purse({ hero }: { hero: Hero }) {
  return (
    <div className="purse slab" aria-label="Кошель">
      <div><Emblem name="res.silver" title="Серебро" />{hero.silver}</div>
      <div className="is-crystal"><Emblem name="res.crystal" title="Кристаллы" />{hero.crystal}</div>
    </div>
  )
}

function Identity({ hero, heroState, onRetryHero }: Pick<Props, 'hero' | 'heroState' | 'onRetryHero'>) {
  return (
    <div className="identity">
      <Link to="/play/hero" className="medal" aria-label="Персонаж">
        {hero?.portrait ? <img src={hero.portrait} alt="" /> : <Emblem name="nav.hero" size={46} style={{ opacity: 0.9 }} />}
      </Link>
      <div className="identity__info">
        <div className="identity__name">
          {hero?.name ?? (heroState === 'loading' ? 'Герой…' : 'Герой')}
          {hero && <small>ур. {hero.level}</small>}
          {hero && <InjuryChip hero={hero} />}
          {hero && (
            <small className="identity__purse">
              <Emblem name="res.silver" size={13} title="Серебро" /> {hero.silver} <Emblem name="res.crystal" size={13} title="Кристаллы" /> {hero.crystal}
            </small>
          )}
        </div>
        {heroState === 'error' && !hero
          ? <p className="identity__error" role="alert">Данные героя не загружены <button type="button" className="linkbtn" onClick={onRetryHero}>Повторить</button></p>
          : <Gauges resources={heroResources(hero)} />}
      </div>
    </div>
  )
}

export function Hud({ hero, heroState, onRetryHero, journalBadge }: Props) {
  const [more, setMore] = useState(false)
  const moreRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  useEffect(() => setMore(false), [pathname])
  useEffect(() => {
    if (!more) return
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !moreRef.current?.contains(e.target as Node)) setMore(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [more])

  const link = (n: NavItem, inMenu = false) => {
    const badge = n.to === '/play/journal' ? journalBadge : n.to === '/play/skills' && hero?.skill_points ? String(hero.skill_points) : undefined
    return (
      <NavLink key={n.to} to={n.to} end={n.end}
        className={({ isActive }) => cx(inMenu ? 'moremenu__item' : 'navbtn', isActive && 'is-active', !inMenu && n.more && 'navbtn--more-only-desktop')}
        aria-label={badge ? `${n.label} (есть новое)` : n.label}>
        <Emblem name={n.emblem} />
        <span aria-hidden="true">{n.short ? <><span className="navbtn__full">{n.label}</span><span className="navbtn__short">{n.short}</span></> : n.label}</span>
        {badge && <span className="gem" aria-hidden="true">{badge}</span>}
      </NavLink>
    )
  }
  // «Ещё» lights up only for sections that live in its menu at this width (Карта has its own button on desktop).
  const moreActive = NAV.some(n => n.more && n.to !== '/play/map' && pathname.startsWith(n.to))
  // Desktop shows the first sections and Карта; the rest (and all «more» on phones) go to «Ещё».
  const desk = NAV.filter(n => !n.more || n.to === '/play/map')
  return (
    <div className="hud iron" role="navigation" aria-label="Игровое меню">
      <div className="hud__row">
        <Identity hero={hero} heroState={heroState} onRetryHero={onRetryHero} />
        {hero && <Purse hero={hero} />}
      </div>
      <nav className="nav" aria-label="Разделы">
        {desk.map(n => link(n))}
        <div className="navmore navmore--always" ref={moreRef}>
          <button type="button" className={cx('navbtn', moreActive && 'is-active')} aria-expanded={more} aria-haspopup="true" aria-controls="hud-more" onClick={() => setMore(v => !v)}>
            <Emblem name="nav.more" /><span>Ещё</span>
          </button>
          {more && <div className="moremenu slab" id="hud-more">{NAV.filter(n => n.more).map(n => link(n, true))}</div>}
        </div>
      </nav>
    </div>
  )
}
